import { Typography } from '@compass/components'
import { useMatchRoute } from '@tanstack/react-router'
import { AppRoute } from 'appRoutes'
import { MainColumn, SetHeader, SubHeader } from 'features/commonUI'
import { useState } from 'react'
import { UNSAFE_PortalProvider } from 'react-aria/PortalProvider'

import { BotDetectionEventsSkeleton } from './BotDetectionEventsSkeleton'
import { BotDetectionOverviewSkeleton } from './BotDetectionOverviewSkeleton'
import { BotDetectionUpsellModal } from './BotDetectionUpsellModal'
import { BotDetectionEventDetailsSkeleton } from './EventDetail/BotDetectionEventDetailsSkeleton'

interface BotDetectionUpsellViewProps {
  subscriptionId: string
  isModalOpen?: boolean
}

/**
 * Static page + skeleton behind boolean.
 * Portals the RAC Overlay into this page so sidebar/header stay interactive.
 * Rendered from the bot-detection layout when Botd is not enabled, so real pages never mount.
 * Also rendered (with `isModalOpen={false}`) while access is still loading, so the transition
 * into the disabled state doesn't jump from a different loading UI to this skeleton + modal.
 */
export function BotDetectionUpsellView({ subscriptionId, isModalOpen = true }: BotDetectionUpsellViewProps) {
  const matchRoute = useMatchRoute()
  const eventDetailsMatch = matchRoute({ to: AppRoute.BotDetectionEventDetails })
  const isEventsPage = !eventDetailsMatch && Boolean(matchRoute({ to: AppRoute.BotDetectionEvents, fuzzy: true }))
  // State (not ref) so Overlay re-renders once the container mounts — getContainer() is null on first paint.
  const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(null)

  return (
    <UNSAFE_PortalProvider getContainer={() => portalContainer}>
      <MainColumn ref={setPortalContainer}>
        <SetHeader
          title={eventDetailsMatch ? eventDetailsMatch.eventId : isEventsPage ? 'Events' : 'Overview'}
          breadcrumb={[
            {
              title: 'Bots & AI agents',
              to: AppRoute.BotDetection,
              params: { subscriptionId },
            },
            ...(eventDetailsMatch
              ? [
                  {
                    title: 'Events',
                    to: AppRoute.BotDetectionEvents,
                    params: { subscriptionId },
                  },
                ]
              : []),
          ]}
        />
        {eventDetailsMatch ? (
          // Renders its own SubHeader (event id, disabled actions), so no generic one here.
          <BotDetectionEventDetailsSkeleton eventId={eventDetailsMatch.eventId} />
        ) : (
          <>
            <SubHeader
              keepRowLayout
              title={
                <Typography variant='heading' size='2xl' as='h2'>
                  {isEventsPage ? 'Events' : 'Overview'}
                </Typography>
              }
              description={
                isEventsPage
                  ? 'Events recorded with Automation Intelligence API. Select a row for details.'
                  : 'Analyze bot traffic trends across your workspace.'
              }
            />
            {isEventsPage ? <BotDetectionEventsSkeleton aria-hidden /> : <BotDetectionOverviewSkeleton aria-hidden />}
          </>
        )}
      </MainColumn>
      <BotDetectionUpsellModal isOpen={isModalOpen} subscriptionId={subscriptionId} />
    </UNSAFE_PortalProvider>
  )
}
